import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaCheck, FaChevronDown } from 'react-icons/fa';
import './CustomSelect.scss';

const CustomSelect = ({ value, onChange, options = [], placeholder = 'Select...', className = '', disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);

  const selected = options.find((o) => o.value === value);
  const label = selected ? selected.label : placeholder;

  const close = useCallback(() => setOpen(false), []);

  const handleOpen = useCallback(() => {
    if (!ref.current) return;
    const triggerRect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const estimatedMenuHeight = Math.min(options.length * 42 + 16, 260) + 16;
    setDropUp(estimatedMenuHeight > spaceBelow && triggerRect.top > spaceBelow);
    setOpen(true);
  }, [options.length]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, close]);

  return (
    <div ref={ref} className={`cs ${open ? 'cs--open' : ''} ${dropUp ? 'cs--drop-up' : ''} ${disabled ? 'cs--disabled' : ''} ${!selected ? 'cs--placeholder' : ''} ${className}`.trim()}>
      <button
        type="button"
        className="cs__trigger"
        onClick={() => { if (disabled) return; open ? close() : handleOpen(); }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="cs__label">{label}</span>
        <FaChevronDown className="cs__chevron" />
      </button>

      {open && (
        <ul ref={menuRef} className="cs__menu" role="listbox">
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <li
                key={opt.value}
                className={`cs__option ${isSelected ? 'cs__option--selected' : ''}`}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (opt.value !== value) onChange(opt.value);
                  close();
                }}
              >
                <span className="cs__option-label">{opt.label}</span>
                {isSelected && <FaCheck className="cs__option-check" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CustomSelect;
